import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { BaseControlValueAccessor, MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { FormSelectOption } from '../../../entities/form-select-option.entity';
import { FormSelectStyle } from '../../../entities/form-select-style.entity';


@Component({
    selector: 'sh-form-select-base',
    templateUrl: './form-select-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormSelectBaseComponent)],
    standalone: true,
    imports: [ReactiveFormsModule]
})
export class ShFormSelectBaseComponent extends BaseControlValueAccessor implements OnInit, OnDestroy {
    private readonly DELETE_INPUT_KEYS: string[] = ['Backspace', 'Delete', 'Clear'];

    private localOptions: FormSelectOption[] | null = null;

    readonly deletePress$: Subject<KeyboardEvent> = new Subject<KeyboardEvent>();

    extraOption: FormSelectOption | null = null;
    selectControl: UntypedFormControl;

    get options(): FormSelectOption[] | null {
        return this.localOptions;
    }

    get classes(): string {
        const classes: string[] = [];

        if (this.style) {
            classes.push(this.style);
        }
        classes.push('sh-form-group__input');

        return classes.join(' ');
    }

    @Input() set options(options: FormSelectOption[] | null) {
        if (options && !options.every(option => typeof option.value === 'string')) {
            throw new Error('ShFormSelectBaseComponent:: value must be Array<FormSelectOption<string>>');
        }

        this.localOptions = options;
        this.baseWriteValue(this.selectControl.value, false);
    }

    @Input() id!: string;
    @Input() placeholderMsg = '';
    @Input() style?: FormSelectStyle = FormSelectStyle.Default;
    @Input() showEmptyOption: boolean = true;
    @Input() emptyOptionLabel: string = '';
    @Input() isDisabled: boolean = false;

    constructor(cd: ChangeDetectorRef) {
        super(cd);
        this.selectControl = new UntypedFormControl();
    }

    ngOnInit(): void {
        this.setupControlValueChangesListener();
        this.initDeletePressSubscriber();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.deletePress$.complete();
    }

    public writeValue(value: any): void {
        this.baseWriteValue(value);
    }

    public handleDeletePress(keyboardEvent: KeyboardEvent): void {
        if (this.DELETE_INPUT_KEYS.includes(keyboardEvent.key)) {
            this.deletePress$.next(keyboardEvent);
        }
    }

    private baseWriteValue(value: any, shouldEmit: boolean = true): void {
        this.extraOption = null;

        if (typeof value !== 'string' || !value.length) {
            this.setEmptyValue(shouldEmit);
            return;
        }

        const stringValue: string = value.toString();

        if (this.options) {
            const keys: string[] = this.options.map(option => option.value);
            if (!keys.includes(stringValue)) {
                console.warn(
                    `[ShFormSelectBaseComponent] Value '${stringValue}' is not present in the provided options: [${keys.join(
                        ', '
                    )}]`
                );
                this.extraOption = { label: stringValue, value: stringValue };
            }
        }

        this.selectControl.setValue(stringValue, { emitEvent: shouldEmit });
    }

    private setupControlValueChangesListener(): void {
        this.selectControl.valueChanges
            .pipe(takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe(result => this.propagateChange(result));
    }

    private initDeletePressSubscriber(): void {
        this.deletePress$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.setEmptyValue();
        });
    }

    private setEmptyValue(shouldEmit = true): void {
        if (this.showEmptyOption) {
            this.selectControl.setValue(null, { emitEvent: shouldEmit });
        } else {
            this.selectControl.setValue(this.options && this.options.length > 0 ? this.options[0].value : null, {
                emitEvent: shouldEmit
            });
        }
    }
}
